import React, { useState, useEffect ,useMemo} from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Collapse, 
  Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PublicIcon from "@mui/icons-material/Public";
import CloudIcon from "@mui/icons-material/Cloud";
import LanIcon from "@mui/icons-material/Lan";
import SecurityIcon from "@mui/icons-material/Security";
import DnsIcon from "@mui/icons-material/Dns";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import StorageIcon from "@mui/icons-material/Storage"; // <-- NEW for AMIs
import MemoryIcon from "@mui/icons-material/Memory";   // <-- NEW for Instances
import { toast } from "react-toastify";
import Layout from "../../layout/layout";
import { api } from "../../services/api";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// --- 1. THE RESOURCE CARD COMPONENT ---
const ResourceCard = ({ label, subtitle, valueToCopy, type, hasChildren, isExpanded, onToggle }) => {
  const handleCopy = (e) => {
    e.stopPropagation(); 
    if (!valueToCopy) return;
    navigator.clipboard.writeText(valueToCopy);
    toast.success(`${label} copied!`, { autoClose: 1500 });
  };

  const getTheme = () => {
    switch (type) {
      case "REGION": return { icon: <PublicIcon />, color: "#0284c7", bg: "#e0f2fe" };
      case "VPC": return { icon: <CloudIcon />, color: "#059669", bg: "#d1fae5" };
      case "FOLDER_SUBNET": return { icon: <FolderOpenIcon />, color: "#8b5cf6", bg: "#ede9fe" };
      case "SUBNET": return { icon: <LanIcon />, color: "#8b5cf6", bg: "#ede9fe" };
      case "FOLDER_SG": return { icon: <FolderOpenIcon />, color: "#ea580c", bg: "#ffedd5" };
      case "SG": return { icon: <SecurityIcon />, color: "#ea580c", bg: "#ffedd5" };
      case "FOLDER_AMI": return { icon: <FolderOpenIcon />, color: "#ca8a04", bg: "#fef08a" }; // Yellow
      case "AMI": return { icon: <StorageIcon />, color: "#ca8a04", bg: "#fef08a" };
      case "FOLDER_INSTANCE": return { icon: <FolderOpenIcon />, color: "#db2777", bg: "#fce7f3" }; // Pink
      case "INSTANCE": return { icon: <MemoryIcon />, color: "#db2777", bg: "#fce7f3" };
      
      default: return { icon: <CloudIcon />, color: "#64748b", bg: "#f1f5f9" };
    }
  };

  const theme = getTheme();
  const isLeaf = !hasChildren;
  const cardWidth = isLeaf ? 250 : 300;

  return (
    <Paper
      onClick={hasChildren ? onToggle : undefined}
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.5,
        width: cardWidth, 
        border: "1px solid",
        borderColor: isExpanded ? theme.color : "grey.300", 
        borderRadius: 2,
        bgcolor: "white",
        boxShadow: isExpanded ? `0px 4px 12px rgba(0, 0, 0, 0.08)` : "0px 2px 4px -1px rgba(0, 0, 0, 0.04)",
        cursor: hasChildren ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: theme.color,
          boxShadow: `0px 4px 12px rgba(0, 0, 0, 0.1)`,
          transform: "translateY(-2px)",
          "& .copy-btn": { opacity: 1 }, 
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minWidth: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: theme.bg,
          color: theme.color,
          mr: 1.5,
        }}
      >
        {theme.icon}
      </Box>

      <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
        <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="700" noWrap>
          {subtitle || "Unnamed Resource"}
        </Typography>
        <Typography variant="caption" fontFamily="monospace" color="textSecondary" noWrap sx={{ display: "block" }}>
          {valueToCopy}
        </Typography>
      </Box>

      {valueToCopy && (
        <Tooltip title="Copy ID" placement="top">
          <IconButton
            className="copy-btn"
            size="small"
            onClick={handleCopy}
            sx={{ opacity: 0.2, transition: "opacity 0.2s", color: theme.color, mr: hasChildren ? 0.5 : 0 }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {hasChildren && (
        <Box display="flex" alignItems="center" color="action.active">
          {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </Box>
      )}
    </Paper>
  );
};

// --- 2. THE RECURSIVE HYBRID TREE ENGINE ---
const VerticalTree = ({ node, expandedNodes, toggleNode }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = !!expandedNodes[node.id];

  return (
    <Box>
      <Box sx={{ position: "relative", zIndex: 2 }}>
        <ResourceCard
          label={node.label}
          subtitle={node.subtitle}
          valueToCopy={node.valueToCopy}
          type={node.type}
          hasChildren={hasChildren}
          isExpanded={isExpanded}
          onToggle={() => toggleNode(node.id)}
        />
      </Box>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          
          {/* HORIZONTAL LAYOUT (For Leaf nodes like Subnets, SGs, AMIs) */}
          {node.childLayout === "horizontal" ? (
            <Box sx={{ position: "relative", ml: 4, pt: 3, pb: 2 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: -16,
                  left: 0,
                  height: 48,
                  width: 24,
                  borderLeft: "2px solid #cbd5e1",
                  borderBottom: "2px solid #cbd5e1",
                  borderBottomLeftRadius: 8,
                  zIndex: 0,
                }}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: 4 }}>
                {node.children.map((child) => (
                  <Box key={child.id} sx={{ position: "relative", zIndex: 2 }}>
                    <ResourceCard
                      label={child.label}
                      subtitle={child.subtitle}
                      valueToCopy={child.valueToCopy}
                      type={child.type}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            
            /* VERTICAL LAYOUT (For Structural nodes like Regions & VPCs) */
            <Box sx={{ position: "relative", ml: 4, pt: 2 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: -16, 
                  bottom: 0,
                  left: 0,
                  borderLeft: "2px solid #cbd5e1",
                  zIndex: 0,
                }}
              />

              {node.children.map((child, index) => {
                const isLast = index === node.children.length - 1;
                
                return (
                  <Box key={child.id || index} sx={{ position: "relative", pl: 4, pb: 2 }}>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 32,
                        left: 0,
                        width: 32,
                        borderTop: "2px solid #cbd5e1",
                        zIndex: 0,
                      }}
                    />

                    {isLast && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 32,
                          bottom: 0,
                          left: -2,
                          width: 4,
                          bgcolor: "#f8fafc", 
                          zIndex: 1,
                        }}
                      />
                    )}

                    <VerticalTree 
                      node={child} 
                      expandedNodes={expandedNodes} 
                      toggleNode={toggleNode} 
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Collapse>
      )}
    </Box>
  );
};

// --- 3. THE MAIN PAGE COMPONENT ---
const MasterDataGuide = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    regions: [], vpcs: [], subnets: [], securityGroups: [], amiImages: [], instances: []
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState({});


  const pageTitle = useMemo(() => {
  try {
    const menus = JSON.parse(sessionStorage.getItem('userMenus') || '[]');
    const matchedMenu = menus.find(
      (m) => m.path?.toLowerCase() === "/awsmasterdata"
    );
    return matchedMenu?.label ?? null;
  } catch {
    return null;
  }
}, []);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        setIsLoading(true);
        const activeAccountId = sessionStorage.getItem("activeAccountId");
        const response = await api.get("/api/AwsEc2Data/GetAwsMasterData", {
          params: { accountId: activeAccountId },
          headers: {
            "X-CORRELATION-ID": crypto.randomUUID(),
            accept: "*/*",
          },
        });
        setData(response.data || {});
      } catch (error) {
        console.error("Failed to fetch master data", error);
        toast.error("Failed to load infrastructure structure.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchMasterData();
  }, []);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  // --- DATA NORMALIZATION (Adding Folders) ---
  const buildNetworkGraph = () => {
    const { regions = [], vpcs = [], subnets = [], securityGroups = [], amiImages = [], instances = [] } = data;
    const query = searchQuery.toLowerCase();

    const sortedRegions = [...regions].sort((a, b) => {
      if (a.regionalDisplayName > b.regionalDisplayName) return -1;
      if (a.regionalDisplayName < b.regionalDisplayName) return 1;
      return 0;
    });

    const graph = sortedRegions.map((region) => {
      const regionId = `region-${region.id}`;
      
      const regionVpcs = vpcs.filter((v) => v.region === region.regionalDisplayName || v.regionId === region.id);
      const vpcNodes = regionVpcs.map((vpc) => {
        const vpcId = `vpc-${vpc.vpcId}`;
        
        const subnetNodes = subnets
          .filter((s) => s.vpcId === vpc.vpcId)
          .map((subnet) => ({
            id: `subnet-${subnet.subnetId}`,
            type: "SUBNET",
            label: `Subnet (${subnet.availabilityZone})`,
            subtitle: subnet.subnetName || subnet.cidrBlock,
            valueToCopy: subnet.subnetId,
            children: []
          }));

        const sgNodes = securityGroups
          .filter((sg) => sg.vpcId === vpc.vpcId)
          .map((sg) => ({
            id: `sg-${sg.groupId}`,
            type: "SG",
            label: "Security Group",
            subtitle: sg.groupName,
            valueToCopy: sg.groupId,
            children: []
          }));

        const vpcChildren = [];
        if (subnetNodes.length > 0) {
          vpcChildren.push({
            id: `folder-subnet-${vpc.vpcId}`,
            type: "FOLDER_SUBNET",
            label: "Subnets",
            subtitle: `${subnetNodes.length} Allocated`,
            childLayout: "horizontal", 
            children: subnetNodes
          });
        }
        if (sgNodes.length > 0) {
          vpcChildren.push({
            id: `folder-sg-${vpc.vpcId}`,
            type: "FOLDER_SG",
            label: "Security Groups",
            subtitle: `${sgNodes.length} Configured`,
            childLayout: "horizontal", 
            children: sgNodes
          });
        }

        return {
          id: vpcId,
          type: "VPC",
          label: "VPC Network",
          subtitle: vpc.vpcName || vpc.cidrBlock,
          valueToCopy: vpc.vpcId,
          children: vpcChildren 
        };
      });

      // 2. Get AMIs for this Region
      const regionAmis = amiImages
        .filter((a) => a.region === region.regionalDisplayName)
        .map((ami) => ({
          id: `ami-${ami.id}`,
          type: "AMI",
          label: ami.platformDetails || "AMI",
          subtitle: ami.name || "Unnamed AMI",
          valueToCopy: ami.amiId,
          children: []
        }));

      // 3. Get Instances for this Region
      const regionInstances = instances
        .filter((i) => i.region === region.regionalDisplayName)
        .map((inst) => ({
          id: `inst-${inst.id}`,
          type: "INSTANCE",
          label: `${inst.vcpUs} vCPU | ${inst.memory}GB RAM`,
          subtitle: inst.instanceType,
          valueToCopy: inst.instanceType,
          children: []
        }));

      // 4. Combine VPCs, AMI Folders, and Instance Folders under the Region Node
      const regionChildren = [...vpcNodes];

      if (regionAmis.length > 0) {
        regionChildren.push({
          id: `folder-ami-${region.id}`,
          type: "FOLDER_AMI",
          label: "AMI Catalog",
          subtitle: `${regionAmis.length} Images Available`,
          childLayout: "horizontal",
          children: regionAmis
        });
      }

      if (regionInstances.length > 0) {
        regionChildren.push({
          id: `folder-inst-${region.id}`,
          type: "FOLDER_INSTANCE",
          label: "Instance Types",
          subtitle: `${regionInstances.length} Types Available`,
          childLayout: "horizontal",
          children: regionInstances
        });
      }

      return {
        id: regionId,
        type: "REGION",
        label: "AWS Region",
        subtitle: region.regionalDisplayName,
        valueToCopy: region.regionalDisplayName,
        children: regionChildren
      };
    });

    if (!searchQuery) return graph;
    return graph.filter(regionNode => JSON.stringify(regionNode).toLowerCase().includes(query));
  };

  const networkGraph = buildNetworkGraph();

  // Auto-expand nodes when searching
  useEffect(() => {
    if (searchQuery.length > 2) {
      const allKeys = {};
      networkGraph.forEach((region) => {
        allKeys[region.id] = true;
        region.children.forEach((child) => {
          allKeys[child.id] = true;
          if (child.children) {
            child.children.forEach((folder) => {
              allKeys[folder.id] = true;
            });
          }
        });
      });
      setExpandedNodes(allKeys);
    } else if (searchQuery.length === 0) {
      setExpandedNodes({}); 
    }
  }, [searchQuery, data]);

  return (
    <Layout pageType="sideBar">
      <Box maxWidth="100%" mx="auto" pt={2} pb={6} px={2}>
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="title" fontWeight="bold" gutterBottom>
           

             {pageTitle && (
  <Typography variant="title" sx={{ m: 0 }}>
    {pageTitle}
</Typography>
)}
          </Typography>
          <Typography color="textSecondary" variant="h5" mt="5px">
            Navigate your network hierarchy. Click cards to expand folders, and hover to copy exact IDs.
          </Typography>
        </Box>

        <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/bulkEC2Provisioning')}
            sx={{ 
              fontWeight: 600, 
              textTransform: "none", 
              borderRadius: 2,
            }}
          >
            Bulk EC2 Upload
          </Button>
          </Box>

        <Paper
          sx={{
            height: "70vh",
            overflowY: "auto",
            overflowX: "hidden", 
            borderRadius: 3,
            border: "1px solid",
            borderColor: "grey.300",
            bgcolor: "#f8fafc", 
            backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)",
            backgroundSize: "20px 20px",
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: 6 // Massive gap between different regions
          }}
        >
          {isLoading ? (
            <Typography color="textSecondary" textAlign="center" mt={6}>
              Constructing topology map...
            </Typography>
          ) : networkGraph.length > 0 ? (
            networkGraph.map((rootNode) => (
              <VerticalTree 
                key={rootNode.id} 
                node={rootNode} 
                expandedNodes={expandedNodes} 
                toggleNode={toggleNode} 
              />
            ))
          ) : (
            <Typography color="textSecondary" textAlign="center" mt={6} bgcolor="white" p={2} borderRadius={2} mx="auto">
              No network resources found matching your search.
            </Typography>
          )}
        </Paper>

      </Box>
    </Layout>
  );
};

export default MasterDataGuide;